import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Shield, Eye, EyeOff, Lock, Mail, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { ROUTE_PATHS } from '@/lib/index';
import { useI18n } from '@/hooks/useI18n';
import { useToast } from '@/hooks/use-toast';

export default function Login() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [checkingAuth, setCheckingAuth] = useState(true);

  useEffect(() => {
    const checkUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('is_admin')
          .eq('id', session.user.id)
          .maybeSingle();
        
        if (profile?.is_admin) {
          navigate(ROUTE_PATHS.ADMIN, { replace: true });
        } else {
          navigate(ROUTE_PATHS.DASHBOARD, { replace: true });
        }
      } else {
        setCheckingAuth(false);
      }
    };
    checkUser();
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (authError) {
        setError('Nieprawidłowy email lub hasło. Spróbuj ponownie.');
        toast({
          title: 'Błąd logowania',
          description: 'Nieprawidłowy email lub hasło.',
          variant: 'destructive',
        });
        setLoading(false);
        return;
      }

      if (authData.user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('is_admin')
          .eq('id', authData.user.id)
          .maybeSingle();

        toast({
          title: 'Zalogowano pomyślnie',
          description: 'Witamy z powrotem w CYF24 Bank!',
        });

        if (profile?.is_admin) {
          navigate(ROUTE_PATHS.ADMIN, { replace: true });
        } else {
          navigate(ROUTE_PATHS.DASHBOARD, { replace: true });
        }
      }
    } catch (err) {
      setError('Wystąpił nieoczekiwany błąd. Spróbuj ponownie.');
      toast({
        title: 'Błąd',
        description: 'Wystąpił nieoczekiwany błąd.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-accent/30 border-t-accent rounded-full animate-spin" />
          <p className="text-muted-foreground">Ładowanie...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex">
      <div className="hidden lg:flex lg:w-1/2 bg-card/50 relative overflow-hidden flex-col justify-between p-12">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            background:
              'radial-gradient(circle at 30% 50%, oklch(0.72 0.14 85 / 0.3) 0%, transparent 60%), radial-gradient(circle at 80% 20%, oklch(0.5 0.2 250 / 0.2) 0%, transparent 50%)',
          }}
        />
        <Link to={ROUTE_PATHS.HOME} className="relative flex items-center gap-3 group">
          <Shield className="w-10 h-10 text-accent transition-transform group-hover:scale-110" />
          <div>
            <span className="text-2xl font-bold text-foreground">CYF24 Bank</span>
            <p className="text-xs text-muted-foreground font-mono">Brytyjski Bank Cyfrowy</p>
          </div>
        </Link>
        <div className="relative space-y-6">
          <h2 className="text-4xl font-bold text-foreground leading-tight">
            Bezpieczny dostęp do
            <br />
            <span className="text-accent">CYF24 Bank</span>
          </h2>
          <p className="text-muted-foreground text-lg">
            Zaloguj się do swojego konta, aby zarządzać finansami z najwyższym poziomem bezpieczeństwa.
          </p>
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Shield className="w-4 h-4 text-accent" />
              Szyfrowanie 256-bit SSL
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Shield className="w-4 h-4 text-accent" />
              Regulowany przez FCA
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Shield className="w-4 h-4 text-accent" />
              Chroniony przez FSCS do £85,000
            </div>
          </div>
        </div>
        <p className="relative text-xs text-muted-foreground">
          © 2026 CYF SOLUTION LTD. Wszelkie prawa zastrzeżone.
        </p>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <Shield className="w-8 h-8 text-accent" />
            <span className="text-xl font-bold text-foreground">CYF24 Bank</span>
          </div>

          <h1 className="text-3xl font-bold text-foreground mb-2">Zaloguj się</h1>
          <p className="text-muted-foreground mb-8">
            Nie masz konta?{' '}
            <Link to={ROUTE_PATHS.REGISTER} className="text-accent hover:underline font-medium">
              Zarejestruj się
            </Link>
          </p>

          {error && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex items-center gap-3 bg-destructive/10 border border-destructive/30 rounded-xl p-4 mb-6"
            >
              <AlertCircle className="w-5 h-5 text-destructive shrink-0" />
              <p className="text-sm text-destructive">{error}</p>
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="twoj@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-12"
                  required
                  autoComplete="email"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Hasło</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10 h-12"
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" className="rounded" />
                <span className="text-muted-foreground">Zapamiętaj mnie</span>
              </label>
              <a href="#" className="text-accent hover:underline">
                Zapomniałeś hasła?
              </a>
            </div>

            <Button
              type="submit"
              className="w-full h-12 bg-accent text-accent-foreground hover:bg-accent/90 font-semibold text-base"
              disabled={loading}
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-accent-foreground/30 border-t-accent-foreground rounded-full animate-spin" />
                  Logowanie...
                </span>
              ) : (
                'Zaloguj się'
              )}
            </Button>
          </form>

          <div className="mt-8 pt-6 border-t border-border/50 text-center">
            <Link
              to={ROUTE_PATHS.HOME}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              ← Powrót do strony głównej
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}